import { Injectable } from '@angular/core';
import { ModuleCardCode } from 'src/app/core/enums';
import { ScreenFunctionPermissionService } from 'src/app/core/services/screen-function-permission.service';
import { MODULE_CARD_ROUTE_DEFINITIONS } from 'src/app/pages/setup/subscription-settings/subscription-packages/assign-packages-privilege/module-cards.constants';

export type ScreenCardPermission = ModuleCardCode[] | null;

/**
 * Manage-account keeps its own multi-stage card resolution (tenant security,
 * AI cards, etc.), so it must be exempt from the generic hub-restriction rule.
 */
const MANAGE_ACCOUNT_PATH = 'pages/shopping-customer/manage-account';

export interface ScreenCardPermissionState {
  /** False when the parent screen is not present in the authenticated menu. */
  screenAssigned: boolean;
  /** `null` means every card; an empty array means no cards. */
  permittedCodes: ScreenCardPermission;
}

@Injectable({
  providedIn: 'root',
})
export class CardPermissionService {
  private readonly screenPermissions = new Map<string, ScreenCardPermission>();
  private cachedUserSnapshot: string | null = null;
  private hubRoutingPaths: Set<string> | null = null;

  constructor(private screenFunctionPermissions: ScreenFunctionPermissionService) { }

  filterCardsByRoute<T extends { code: ModuleCardCode }>(cards: T[], routingPath: string): T[] {
    if (!Array.isArray(cards) || !cards.length || !routingPath) {
      return cards;
    }

    if (this.screenFunctionPermissions.bypassesScreenPermissions()) {
      return cards;
    }

    const permissionState = this.getScreenCardPermissionState(routingPath);
    if (!permissionState.screenAssigned) {
      // Before authentication there is no menu to evaluate. Once authenticated,
      // a missing screen is not equivalent to the explicit `cards: null` grant.
      return localStorage.getItem('ACCESS_TOKEN') ? [] : cards;
    }

    if (this.isRestrictedEmptyHub(routingPath, permissionState)) {
      return [];
    }

    const permittedCodes = permissionState.permittedCodes;
    if (permittedCodes === null) {
      return cards;
    }

    if (!permittedCodes.length) {
      return [];
    }

    const allowedSet = new Set<ModuleCardCode>(permittedCodes);
    return cards.filter((card) => allowedSet.has(card.code));
  }

  getPermittedCardCodes(routingPath: string): ScreenCardPermission {
    if (!routingPath) {
      return null;
    }

    this.ensurePermissionsLoaded();
    const normalizedPath = this.normalizeRoutingPath(routingPath);

    if (!this.screenPermissions.has(normalizedPath)) {
      return null;
    }

    return this.screenPermissions.get(normalizedPath) ?? null;
  }

  /**
   * Returns a state that keeps an unassigned parent screen distinct from
   * `cards: null`. The card hubs retain their historical permissive filtering,
   * while direct child-route checks use this stricter state.
   */
  getScreenCardPermissionState(routingPath: string): ScreenCardPermissionState {
    if (this.screenFunctionPermissions.bypassesScreenPermissions()) {
      return { screenAssigned: true, permittedCodes: null };
    }

    if (!routingPath) {
      return { screenAssigned: false, permittedCodes: [] };
    }

    this.ensurePermissionsLoaded();
    const normalizedPath = this.normalizeRoutingPath(routingPath);
    if (!normalizedPath || !this.screenPermissions.has(normalizedPath)) {
      return { screenAssigned: false, permittedCodes: [] };
    }

    return {
      screenAssigned: true,
      permittedCodes: this.screenPermissions.get(normalizedPath) ?? null,
    };
  }

  hasCardAccess(routingPath: string, cardCode: ModuleCardCode): boolean {
    const state = this.getScreenCardPermissionState(routingPath);
    if (!state.screenAssigned) {
      return false;
    }

    if (this.isRestrictedEmptyHub(routingPath, state)) {
      return false;
    }

    return state.permittedCodes === null || state.permittedCodes.includes(cardCode);
  }

  /**
   * Whether a granted screen should be hidden from the navigation because it
   * exposes no usable card. Covers the explicit empty allowlist as well as the
   * scoped rule for card-using roles (see {@link isRestrictedEmptyHub}).
   */
  shouldHideAssignedScreen(routingPath: string): boolean {
    const state = this.getScreenCardPermissionState(routingPath);
    if (!state.screenAssigned) {
      return false;
    }
    if (Array.isArray(state.permittedCodes) && state.permittedCodes.length === 0) {
      return true;
    }
    return this.isRestrictedEmptyHub(routingPath, state);
  }

  /** True when the routing path is a known card hub. */
  isCardHub(routingPath: string): boolean {
    const normalizedPath = this.normalizeRoutingPath(routingPath);
    return !!normalizedPath && this.getHubRoutingPaths().has(normalizedPath);
  }

  /**
   * True when the current role already relies on explicit card allowlists
   * (i.e. at least one granted screen lists specific cards). Roles that never
   * used the feature keep the historical permissive behaviour.
   */
  roleUsesCardRestrictions(): boolean {
    this.ensurePermissionsLoaded();
    for (const codes of this.screenPermissions.values()) {
      if (Array.isArray(codes) && codes.length > 0) {
        return true;
      }
    }
    return false;
  }

  /**
   * Scoped restriction: for a role that uses card allowlists, a granted card
   * hub that carries no explicit allowlist (`cards: null`) is treated as
   * "no access". Manage-account is exempt because it owns its resolution.
   */
  private isRestrictedEmptyHub(routingPath: string, state: ScreenCardPermissionState): boolean {
    const normalizedPath = this.normalizeRoutingPath(routingPath);
    return (
      state.screenAssigned &&
      state.permittedCodes === null &&
      !!normalizedPath &&
      normalizedPath !== MANAGE_ACCOUNT_PATH &&
      this.isCardHub(normalizedPath) &&
      !this.isOwnerOrAdmin() &&
      this.roleUsesCardRestrictions()
    );
  }

  /**
   * The customer owner (CUST) and admins receive a package-derived menu whose
   * `cards` come from the package superset, not from role allowlists. They must
   * never be trimmed by the role-scoped hub restriction.
   */
  private isOwnerOrAdmin(): boolean {
    try {
      const user = JSON.parse(localStorage.getItem('CURRENT_USER') || 'null');
      const types = user?.userTypes;
      if (!Array.isArray(types)) {
        return false;
      }
      return types.some((type: any) => type?.code === 'CUST' || type?.code === 'ADMIN');
    } catch (error) {
      console.warn('Failed to read user types', error);
      return false;
    }
  }

  private getHubRoutingPaths(): Set<string> {
    if (!this.hubRoutingPaths) {
      this.hubRoutingPaths = new Set(
        MODULE_CARD_ROUTE_DEFINITIONS.map((definition) =>
          this.normalizeRoutingPath(definition.screenRoutingPath),
        ).filter((path): path is string => !!path),
      );
    }
    return this.hubRoutingPaths;
  }

  private ensurePermissionsLoaded(): void {
    const storedUser = localStorage.getItem('CURRENT_USER');
    if (!storedUser) {
      this.resetPermissions();
      return;
    }

    if (this.cachedUserSnapshot === storedUser && this.screenPermissions.size > 0) {
      return;
    }

    this.cachedUserSnapshot = storedUser;
    this.screenPermissions.clear();

    try {
      const parsedUser = JSON.parse(storedUser);
      const menus = parsedUser?.userPrivilage?.menu;
      if (!Array.isArray(menus)) {
        return;
      }

      menus.forEach((menu: any) => {
        if (!Array.isArray(menu?.screens)) {
          return;
        }
        menu.screens.forEach((screen: any) => {
          const normalizedPath = this.normalizeRoutingPath(screen?.routingPath);
          if (!normalizedPath) {
            return;
          }
          const permission = this.resolveScreenPermission(screen?.cards);
          if (permission === undefined) {
            return;
          }
          this.screenPermissions.set(normalizedPath, permission);
        });
      });
    } catch (error) {
      console.warn('Failed to load card permissions', error);
    }
  }

  private resetPermissions(): void {
    this.cachedUserSnapshot = null;
    this.screenPermissions.clear();
  }

  private resolveScreenPermission(cardsValue: unknown): ScreenCardPermission | undefined {
    if (cardsValue === undefined) {
      return null;
    }
    if (cardsValue === null) {
      return null;
    }
    if (typeof cardsValue === 'string') {
      const trimmed = cardsValue.trim();
      if (!trimmed || trimmed.toLowerCase() === 'null') {
        return null;
      }
      if (trimmed.startsWith('[')) {
        return this.parseCardCodes(trimmed);
      }
      return this.parseCardCodes(trimmed);
    }
    if (Array.isArray(cardsValue)) {
      return this.normalizeCardCodes(cardsValue);
    }
    return this.parseCardCodes(cardsValue as string);
  }

  private parseCardCodes(cardsValue: unknown): ModuleCardCode[] {
    if (!cardsValue) {
      return [];
    }

    if (Array.isArray(cardsValue)) {
      return this.normalizeCardCodes(cardsValue);
    }

    if (typeof cardsValue === 'string') {
      const trimmed = cardsValue.trim();
      if (!trimmed) {
        return [];
      }
      try {
        if (trimmed.startsWith('[')) {
          const parsed = JSON.parse(trimmed);
          return Array.isArray(parsed) ? this.normalizeCardCodes(parsed) : [];
        }
      } catch (error) {
        console.warn('Failed to parse cards JSON', error);
      }
      return this.normalizeCardCodes(trimmed.split(',').map((token) => token.trim()));
    }

    return [];
  }

  private normalizeCardCodes(codes: string[]): ModuleCardCode[] {
    if (!Array.isArray(codes)) {
      return [];
    }

    const uniqueCodes = new Set<string>();
    return codes
      .map((code) => (typeof code === 'string' ? code.trim() : ''))
      .filter((code) => {
        if (!code || uniqueCodes.has(code)) {
          return false;
        }
        uniqueCodes.add(code);
        return true;
      })
      .map((code) => code as ModuleCardCode);
  }

  private normalizeRoutingPath(path?: string): string | null {
    if (!path || typeof path !== 'string') {
      return null;
    }
    return path.trim().replace(/^\//, '').toLowerCase();
  }
}


